import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  type Finding,
  type GroupedFinding,
  groupFindings,
  type RulebookResult,
} from '../../../api/invoiceValidationClient'
import { pluralCategory } from './VerdictHeader'

/**
 * Der Befund-Report — gelesen wie die Ausgabe eines Compilers: je Regelwerk eine Kopfzeile mit Symbol, Kennung und
 * Lage, darunter seine Befunde als `Kennung · Text`.
 *
 * 🔴 Gruppiert nach `results[].format` (`rulebookResults`). Die flache Liste `errors[]` nennt das Regelwerk je Eintrag
 * nicht — daraus allein liesse sich nicht sagen, WER etwas gefunden hat. Fehlt `results[]` (Abbruch beim Lesen, alte
 * Antwort), zeigt der Report die flache Liste ohne Gruppenkopf, wie bisher.
 *
 * 🔴 Kein Befund geht verloren: Was in der flachen Liste steht, aber in keiner Gruppe (etwa eine eigene Vorpruefung
 * ausserhalb der Regelwerke), steht als letzte Gruppe „Weitere Befunde" da.
 *
 * ⚪ EINE Huelle (`rounded-lg border`), Gruppen durch Haarlinien getrennt — keine Karte je Gruppe (Karte in Karte).
 * tabelle-ausnahme: eine Befundliste ist ein Detail-Aufriss, keine Menge zum Suchen (`UI_GUIDELINES §5`).
 */
export interface FindingsReportProps {
  /** Die flache Befundliste der Antwort (`errors[]` bzw. die Lesefehler aus Schritt 1). */
  findings: Finding[]
  warnings: Finding[]
  /** Je Regelwerk Urteil und Befunde — fehlt es, wird nicht gruppiert. */
  rulebookResults?: RulebookResult[]
}

/** Schluessel eines Befunds fuer den Abgleich zwischen flacher Liste und Gruppen. */
function findingKey(f: Finding): string {
  return `${f.rule ?? f.code}\u0000${f.message}`
}

/**
 * Die Befunde der flachen Liste, die in keiner Gruppe vorkommen — als Multimenge gezaehlt, damit eine Meldung, die
 * zweimal flach und einmal in einer Gruppe steht, einmal uebrig bleibt.
 */
function leftoverFindings(flat: readonly Finding[], groups: readonly RulebookResult[]): Finding[] {
  const inGroups = new Map<string, number>()
  for (const g of groups) {
    for (const f of g.findings) inGroups.set(findingKey(f), (inGroups.get(findingKey(f)) ?? 0) + 1)
  }
  const rest: Finding[] = []
  for (const f of flat) {
    const k = findingKey(f)
    const left = inGroups.get(k) ?? 0
    if (left > 0) inGroups.set(k, left - 1)
    else rest.push(f)
  }
  return rest
}

export function FindingsReport({ findings, warnings, rulebookResults }: FindingsReportProps) {
  const { t, i18n } = useTranslation()
  const groupedWarnings = groupFindings(warnings)
  const grouped = rulebookResults && rulebookResults.length > 0
  const rest = grouped ? leftoverFindings(findings, rulebookResults) : findings
  const restRows = groupFindings(rest)

  if (!grouped && restRows.length === 0 && groupedWarnings.length === 0) return null

  const countText = (n: number) =>
    t(`invoiceValidation.bericht.befunde.${pluralCategory(i18n.language, n)}`, { n })

  return (
    <div
      className="@container rounded-lg border border-border bg-card"
      data-testid="invoice-validation-report"
    >
      {grouped &&
        rulebookResults.map((r) => {
          const rows = groupFindings(r.findings)
          // 🔴 Drei Lagen: lief nicht (`valid: null` oder `skipped`) · fand etwas · fand nichts.
          const state: 'notRun' | 'failed' | 'passed' =
            r.valid === null || r.skipped
              ? 'notRun'
              : r.valid === false || rows.length > 0
                ? 'failed'
                : 'passed'
          const status =
            state === 'notRun'
              ? r.skipped
                ? t('invoiceValidation.bericht.nichtGeprueftGrund', {
                    defaultValue: 'nicht geprüft ({{grund}})',
                    grund: r.skipped,
                  })
                : t('invoiceValidation.bericht.nichtGeprueft', 'nicht geprüft')
              : rows.length > 0
                ? countText(rows.length)
                : t('invoiceValidation.bericht.keine', 'keine Befunde')
          return (
            <GroupSection
              key={r.rulebook}
              title={r.rulebook}
              titleMono
              state={state}
              status={status}
              rows={rows}
              kind="error"
            />
          )
        })}
      {restRows.length > 0 && (
        <GroupSection
          title={grouped ? t('invoiceValidation.bericht.weitere', 'Weitere Befunde') : undefined}
          state="failed"
          status={grouped ? countText(restRows.length) : undefined}
          rows={restRows}
          kind="error"
        />
      )}
      {groupedWarnings.length > 0 && (
        <GroupSection
          title={t('invoiceValidation.bericht.warnungen', 'Warnungen')}
          state="warning"
          rows={groupedWarnings}
          kind="warning"
        />
      )}
    </div>
  )
}

const STATE_ICON = {
  // 🔴 `green-800`, nicht `success`: gemessen 2026-10-10 hatte `success` auf Weiss 2.98:1 (< 3:1 fuer Symbole).
  passed: { icon: CheckCircle2, className: 'text-green-800' },
  failed: { icon: XCircle, className: 'text-destructive' },
  notRun: { icon: AlertTriangle, className: 'text-muted-foreground' },
  warning: { icon: AlertTriangle, className: 'text-muted-foreground' },
} as const

/**
 * Eine Gruppe: Kopfzeile (Symbol · Regelwerk · Lage), darunter die Zeilen. Ohne Titel (flache Liste) keine Kopfzeile.
 * Gruppen trennt eine Haarlinie (`border-t` ab der zweiten).
 */
function GroupSection({
  title,
  titleMono,
  state,
  status,
  rows,
  kind,
}: {
  title?: string
  titleMono?: boolean
  state: keyof typeof STATE_ICON
  status?: string
  rows: GroupedFinding[]
  kind: 'error' | 'warning'
}) {
  const { icon: Icon, className } = STATE_ICON[state]
  return (
    <section
      className="border-t border-border first:border-t-0"
      data-testid="invoice-validation-report-group"
      data-state={state}
    >
      {title && (
        <h3
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-4 py-2.5 text-sm @md:px-6"
          data-testid="invoice-validation-report-group-head"
        >
          <Icon className={`h-4 w-4 shrink-0 self-center ${className}`} aria-hidden="true" />
          <span
            className={`min-w-0 font-medium [overflow-wrap:anywhere] ${titleMono ? 'font-mono text-xs' : ''}`}
          >
            {title}
          </span>
          {status && (
            <span
              className="text-muted-foreground tabular-nums"
              data-testid="invoice-validation-report-group-status"
            >
              {`— ${status}`}
            </span>
          )}
        </h3>
      )}
      {rows.length > 0 && (
        <div className={title ? 'border-t border-border' : ''}>
          {rows.map(({ finding, count }) => (
            <FindingRow
              key={`${kind}-${finding.rule ?? finding.code}-${finding.message}`}
              finding={finding}
              count={count}
              kind={kind}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * Die Meldung ohne ihr eigenes Kennungs-Praefix.
 *
 * ⚪ Manche Meldungen stellen die Kennung in eckigen Klammern voran (`[BR-DE-15] Das Element …`). Links daneben steht
 * sie bereits in der Mono-Spalte. Abgeschnitten wird NUR, wenn das Praefix exakt diese Kennung ist — samt dem Trenner
 * dahinter: EN16931-CEN schreibt `[BR-CO-26]-In order …`, KoSIT `[BR-DE-16] Wenn …` (Issue API 2026-10-12, Regelkopf).
 */
function withoutIdPrefix(message: string, ruleId: string): string {
  const prefix = `[${ruleId}]`
  return message.startsWith(prefix)
    ? message.slice(prefix.length).replace(/^\s*-?\s*/, '')
    : message
}

/**
 * Eine Zeile des Reports: `Kennung · n×` links, der Text daneben, der Feldpfad dahinter.
 *
 * 🔴 Ist der REPORT schmaler als `@md` (28 rem), steht die Kennung UEBER der Meldung (Hallmark-Audit 2026-10-10 v1.71.3,
 * Punkt 1) — sonst wird der Text zur Wortsaeule. Seit 2026-10-10 misst eine Container-Abfrage den
 * Report statt des Bildschirms — er entscheidet, wo er steht, nicht der Bildschirm. Mono
 * nur fuer Kennung und Feldpfad.
 */
function FindingRow({
  finding,
  count,
  kind,
}: {
  finding: Finding
  /** Wie oft die API diesen Befund gemeldet hat (`groupFindings`). */
  count: number
  kind: 'error' | 'warning'
}) {
  const { t } = useTranslation()
  const ruleId = finding.rule ?? finding.code
  return (
    <div
      className="flex flex-col gap-1 border-b border-border px-4 py-3 last:border-b-0 @md:flex-row @md:flex-wrap @md:items-baseline @md:gap-x-3 @md:px-6"
      data-testid="invoice-validation-finding"
    >
      <span
        className="flex min-w-0 items-baseline gap-1.5 text-xs @md:shrink-0"
        data-testid="invoice-validation-finding-id"
      >
        {/* Eine Kennung ohne Trennstelle (`BR-FR-05_BT-22-1`) bricht notfalls mitten im Wort. */}
        <span
          className={`min-w-0 font-mono [overflow-wrap:anywhere] ${kind === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {ruleId}
        </span>
        {count > 1 && (
          <>
            <span className="text-muted-foreground" aria-hidden="true">
              ·
            </span>
            <span
              className="text-muted-foreground tabular-nums"
              title={t('invoiceValidation.befund.mehrfachTitel', {
                defaultValue: '{{n}}-mal gemeldet',
                n: count,
              })}
              data-testid="invoice-validation-finding-count"
            >
              {t('invoiceValidation.befund.mehrfach', { defaultValue: '{{n}}×', n: count })}
            </span>
          </>
        )}
      </span>
      <span
        className="min-w-0 text-sm [overflow-wrap:anywhere] @md:flex-1"
        data-testid="invoice-validation-finding-message"
      >
        {withoutIdPrefix(finding.message, ruleId)}
      </span>
      {/* Der Feldpfad sagt, WO im Dokument es klemmt — nur zeigen, wenn er da ist. */}
      {finding.field && (
        <span className="min-w-0 font-mono text-xs text-muted-foreground [overflow-wrap:anywhere] @md:shrink-0">
          {finding.field}
        </span>
      )}
    </div>
  )
}
